import { ICalendar } from 'src/entities/calendar';
import { IDTO, IResponseDto } from 'src/entities/common';
import { AppLocale } from 'src/models/modules';
import { IFilterType, IReservationDetails, IReservationDetailsList, IReservationModifyRequest } from 'src/entities/reservation';
import { IReservationDto } from '../../entities/reservation';
import { setTimeOnDate } from '../dateTime';
import fetcher, { ContentType } from '../fetcher';
import { StepType } from '../getStepOrder';
import { apiLanguages } from '../translations';
import { IPaidTransactionResponse, IPendingReservation, IReservation, IReservationModel, IReservationQueryOptions, IReservationRequest, IReservationResponse, IReservationSaveRequest, isSignUpUser, isWithinModificationPeriod, populateReservations } from './../../entities/reservation';
import { ISignupUser, IUser, IUserBase } from './../../entities/user';
import { getQueryParam } from './../queryString';
import { accessToken } from './authorization';
import { handleError, handleModifyReservationError } from './errorHandler';
import { login, signUp } from './user';

export interface IPostReservationParams {
    reservations: IReservation[];
    user: IUserBase | ISignupUser;
    calendars?: ICalendar[];
    shouldSignUp?: boolean;
    termsAccepted?: boolean;
    language?: string;
    setUser?: (userData: IUser | null) => void;
}

export const postReservation = async (
    { 
        reservations,
        user,
        calendars,
        shouldSignUp,
        termsAccepted,
        language,
        setUser
    }: IPostReservationParams): Promise<IReservationResponse[]> => {

    if (shouldSignUp && isSignUpUser(user)) {
        await signUp(user).then(
            async () => {
                if (setUser) {
                    await login({
                        username: user.Email,
                        password: user.Password,
                        keepLoggedIn: false
                    }).then(result => setUser(result));
                }
            },
            (reason) => {
                return Promise.reject(reason);
            }
        );
    }
    const userWithoutPassword = Object.keys(user).reduce((previous, current) => current === 'Password' ? previous : {...previous, [current]: user[current as keyof (IUserBase | ISignupUser)]}, {}) as (IUserBase | ISignupUser);

    const body: IReservationSaveRequest = {
        User: userWithoutPassword,
        Reservations: reservations,
        TermsAccepted: termsAccepted ?? false
    }

    const token = await accessToken();
    let url = `/Reservations`;

    if (language) {
        const isIframe = getQueryParam('Mode') === 'iframe';
        const fallbackLanguage = AppLocale.En;
        const currentLanguage = Object.keys(apiLanguages).includes(language) ? language : fallbackLanguage;

        if (isIframe) {
            url += `?culture=${apiLanguages[currentLanguage as keyof typeof apiLanguages]}`;
        }
    }

    return fetcher.post<IDTO<IReservationResponse[]>>(url, body, ContentType.Json, token)
        .then(response => {
            if (calendars) {
                populateReservations(response.Data, calendars)
            }
            return response.Data;
        }, (errorDto: IResponseDto) => {
            return Promise.reject(handleError(errorDto));
        });
}

export const modifyReservation = async (request: IReservationModifyRequest): Promise<undefined> => {
    let token;
    let url;

    if (request.ClientKey) {
        url = '/Reservations/' + request.Reservation.Id + '/ByClientKey';
    } else {
        url = '/Reservations/' + request.Reservation.Id;
        token = await accessToken();
        if (!token) {
            return;
        }
    }

    if (!isWithinModificationPeriod(request.Reservation)) {
        return;
    }

    return fetcher.put<IDTO<IReservationSaveRequest>>(url, request, ContentType.Json, token)
        .then(() => undefined, (errorDto: IResponseDto) => Promise.reject(handleModifyReservationError(errorDto)))
}

export const getReservationById = (id: number): Promise<IReservationDetails> => {
    return fetcher.get<IReservationDto>(`/Reservations/${id}`)
        .then((respJson) => respJson.Data);
}

export const getPreReservation = async (clientKey: string): Promise<IReservationDetails[]> => {
    return fetcher.get<IDTO<IReservationDetails[]>>(`/PreReservations/ByClientKey/${clientKey}`)
        .then((restpJson) => restpJson.Data);
}

export const getPaidTransaction = async (request: IReservationRequest): Promise<IPaidTransactionResponse | null> => {
    
    let token;
    let url = `/Reservations/${request.Id}/PaidTransaction`;

    if (request.ClientKey) {
        url = `${url}/ByClientKey?clientKey=${request.ClientKey}`;
    } else {
        token = await accessToken();
        if (!token) {
            return null;
        }
    }

    return fetcher.get<IDTO<IPaidTransactionResponse>>(url, token)
        .then((restpJson) => restpJson.Data);
}

export const deleteReservation = async (request: IReservationRequest): Promise<any> => {

    let args: [string, string?];

    if (request.ClientKey) {
        args = [`/Reservations/${request.Id}/ByClientKey?clientKey=${request.ClientKey}`];
    } else {
        const token = await accessToken();
        if (!token) {
            return null;
        }
        args = [`/Reservations/${request.Id}`, token];
    }

    return fetcher.delete(...args).catch(() => null)
}

const getFilterInterval = (now: string, type: IFilterType): { startTime?: string; endTime?: string} => (
    {
        'all': {},
        'future': { startTime: now },
        'past': { endTime: now }
    }[type]
)

export const getReservations = async (options: IReservationQueryOptions): Promise<IReservationDetailsList | null> => {

    const params = generateParamsFromQuery(options);
    let token;
    let url;

    if (options.clientKey) {
        url = `/Users/Reservations/ByClientKey/${options.clientKey}?`;
    } else {
        url = '/Users/Reservations?';
        token = await accessToken();
        if (!token) {
            return null;
        }
    }

    return fetcher.get<IDTO<IReservationDetailsList>>(`${url}${params}`, token)
        .then(respJson => respJson.Data)
}

export const reservationModelToRequest = (model: IReservationModel): IReservationDetails => {
    const calendar = model[StepType.Calendar];
    const appointment = model[StepType.Date];
    const questions = model[StepType.CustomerData];
    const event = model[StepType.Timetable];
    const service = model[StepType.Service] || model[StepType.Timetable]!.service;

    const getArriveTime = (date: string) =>
        questions && questions.arriveTime && setTimeOnDate(date, questions.arriveTime)
        || date;

    return {
        Id: 0,
        StartTime: appointment ? appointment.startTime : event!.schedule.Start,
        EndTime: appointment ? appointment.endTime : event!.schedule.End,
        ArriveTime: getArriveTime(appointment ? appointment.startTime : event!.schedule.Start),
        CalendarId: calendar?.Id,
        Calendar: calendar,
        Service: service,
        Title: service.Name,
        ServiceId: service.Id,
        ScheduleId: event && event.schedule.Id,
        PersonCount: appointment ? appointment.personCount : event!.personCount,
        ...(questions && { Questions: questions.answers })
    };
}

export const reservationModelToPendingRequest = (model: IReservationModel): IPendingReservation => {
    const calendar = model[StepType.Calendar];
    const appointment = model[StepType.Date];
    const event = model[StepType.Timetable];
    return appointment ? {
        StartTime: appointment.startTime,
        PersonCount: appointment.personCount,
        CalendarId: calendar?.Id,
        ServiceId: model.service!.Id
    } : {
            StartTime: event!.schedule.Start,
            PersonCount: event!.personCount,
            CalendarId: calendar && calendar.Id,
            ServiceId: model.service!.Id
        }
}

const generateParamsFromQuery = ({ pagination, filter, clientKey }: IReservationQueryOptions): string => {
    const params = new URLSearchParams();
    const interval = getFilterInterval(new Date().toISOString(), filter.type);

    for (const key in interval) {
        if (Object.hasOwn(interval, key)) {
            const value = interval[key as keyof typeof interval];
            if (typeof value === 'string') {
                params.append(key, value)
            }
        }
    }

    params.append('isAscending', String(filter.isAscending));

    for (const key in pagination) {
        if (Object.hasOwn(pagination, key)) {
            const value = pagination[key as keyof typeof pagination]
            if (value) {
                params.append(key, value.toString())
            }
        }
    }

    if (clientKey) {
        params.append('clientKey', clientKey)
    }

    return params.toString();
}